import { useState } from 'react';
import {
  useGetAllVisitorTestsQuery,
  useGetVisitorTestsByVisitorIdQuery,
  useGetVisitorTestsByClientIdQuery,
  useCreateVisitorTestMutation,
  useUpdateVisitorTestMutation,
  useDeleteVisitorTestMutation,
} from '@/api/visitorTestApi';
import type { VisitorTest, VisitorTestRequest } from '@/api/visitorTestApi';
import { useGetAllVisitorsQuery } from '@/api/visitorApi';
import { useGetClientsQuery } from '@/api/clientApi';
import { useListTestsQuery } from '@/api/testApi';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Trash2, Edit2, Plus } from 'lucide-react';
import { formatUsDate } from '@/utils/date';
import { useSelector } from 'react-redux';
import type { RootState } from '@/store';

type FilterType = 'all' | 'client' | 'visitor';

const VisitorTestsList = () => {
  const { user } = useSelector((state: RootState) => state.auth);
  const isClientRole = user?.role === "CLIENT" || user?.role === "CLIENT_EMPLOYEE";

  const [filterType, setFilterType] = useState<FilterType>(isClientRole ? 'client' : 'all');
  const [selectedClientId, setSelectedClientId] = useState<number | null>(isClientRole ? (user?.clientId ?? null) : null);
  const [selectedVisitorId, setSelectedVisitorId] = useState<number | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingTest, setEditingTest] = useState<VisitorTest | null>(null);
  const [formData, setFormData] = useState<VisitorTestRequest>({
    visitorId: 0,
    testTypeId: 0,
    testName: '',
    status: 'NOT_STARTED',
    resultDocumentUrl: '',
    otherDocuments: [],
    notes: '',
    uploadedBy: undefined,
    scheduledDate: '',
    completedDate: '',
  });

  const { data: allTests = [], isLoading: testsLoading, refetch: refetchTests } = useGetAllVisitorTestsQuery(undefined, {
    skip: isClientRole,
  });
  const { data: visitorTests = [] } = useGetVisitorTestsByVisitorIdQuery(selectedVisitorId || 0, {
    skip: !selectedVisitorId || filterType !== 'visitor',
  });
  const { data: clientTests = [] } = useGetVisitorTestsByClientIdQuery(selectedClientId || 0, {
    skip: !selectedClientId || (filterType !== 'client' && !isClientRole),
  });
  const { data: visitors = [] } = useGetAllVisitorsQuery();

  const { data: clients = [] } = useGetClientsQuery();
  const { data: tests = [] } = useListTestsQuery();
  const [createTest] = useCreateVisitorTestMutation();
  const [updateTest] = useUpdateVisitorTestMutation();
  const [deleteTest] = useDeleteVisitorTestMutation();

  // Combine and filter visitors for the selection dropdown
  const filteredVisitors = isClientRole
    ? visitors.filter(v => v.clientId === user?.clientId)
    : visitors;

  const displayedTests =
    isClientRole ? clientTests :
      filterType === 'client' ? clientTests :
        filterType === 'visitor' ? visitorTests :
          allTests;

  const handleOpenDialog = (test?: VisitorTest) => {
    if (test) {
      setEditingTest(test);
      setFormData({
        visitorId: test.visitorId,
        testTypeId: test.testTypeId,
        testName: test.testName,
        status: test.status,
        resultDocumentUrl: test.resultDocumentUrl || '',
        otherDocuments: test.otherDocuments || [],
        notes: test.notes || '',
        uploadedBy: test.uploadedBy,
        scheduledDate: test.scheduledDate || '',
        completedDate: test.completedDate || '',
      });
    } else {
      setEditingTest(null);
      setFormData({
        visitorId: isClientRole ? (filteredVisitors[0]?.id || 0) : 0,
        testTypeId: 0,
        testName: '',
        status: 'NOT_STARTED',
        resultDocumentUrl: '',
        otherDocuments: [],
        notes: '',
        uploadedBy: undefined,
        scheduledDate: '',
        completedDate: '',
      });
    }
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingTest(null);
  };

  const handleSelectVisitor = (visitorId: string) => {
    const visitor = visitors.find(v => v.id === parseInt(visitorId));
    if (visitor) {
      setFormData({
        ...formData,
        visitorId: visitor.id,
      });
    }
  };

  const handleSelectTest = (testId: string) => {
    const test = tests.find(t => t.id === parseInt(testId));
    if (test) {
      setFormData({
        ...formData,
        testTypeId: test.id,
        testName: test.testName,
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingTest) {
        await updateTest({
          id: editingTest.id,
          data: formData,
        }).unwrap();
      } else {
        await createTest(formData).unwrap();
      }
      handleCloseDialog();
      refetchTests();
    } catch (error) {
      console.error('Error saving visitor test:', error);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this test?')) {
      try {
        await deleteTest(id).unwrap();
        refetchTests();
      } catch (error) {
        console.error('Error deleting visitor test:', error);
      }
    }
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'NOT_STARTED':
        return 'secondary';
      case 'WAITING_FOR_RESULT':
        return 'outline';
      case 'RESULT_UPLOADED':
        return 'default';
      case 'READY_TO_REVIEW':
        return 'default';
      case 'CANCELED':
        return 'destructive';
      default:
        return 'secondary';
    }
  };

  const getStatusLabel = (status: string) => {
    return status.replace(/_/g, ' ');
  };

  return (
    <div className="space-y-6 p-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">Visitor Tests Management</h1>
        <Button onClick={() => handleOpenDialog()} className="gap-2">
          <Plus className="w-4 h-4" />
          Add Test
        </Button>
      </div>

      {!isClientRole && (
        <div className="flex gap-4">
          <Select
            value={filterType}
            onValueChange={(value) => {
              setFilterType(value as FilterType);
              setSelectedClientId(null);
              setSelectedVisitorId(null);
            }}
          >
            <SelectTrigger className="w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Tests</SelectItem>
              <SelectItem value="client">Filter by Client</SelectItem>
              <SelectItem value="visitor">Filter by Visitor</SelectItem>
            </SelectContent>
          </Select>

          {filterType === 'client' && (
            <Select
              value={selectedClientId?.toString() || ''}
              onValueChange={(value) => setSelectedClientId(value ? parseInt(value) : null)}
            >
              <SelectTrigger className="w-72">
                <SelectValue placeholder="Select client..." />
              </SelectTrigger>
              <SelectContent>
                {clients.map((client: any) => (
                  <SelectItem key={client.id} value={client.id.toString()}>
                    {client.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {filterType === 'visitor' && (
            <Select
              value={selectedVisitorId?.toString() || ''}
              onValueChange={(value) => setSelectedVisitorId(value ? parseInt(value) : null)}
            >
              <SelectTrigger className="w-72">
                <SelectValue placeholder="Select visitor..." />
              </SelectTrigger>
              <SelectContent>
                {visitors.map((visitor: any) => (
                  <SelectItem key={visitor.id} value={visitor.id.toString()}>
                    {visitor.firstName} {visitor.lastName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      )}

      {testsLoading ? (
        <div>Loading tests...</div>
      ) : (
        <div className="rounded-md border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Test Name</TableHead>
                <TableHead>Visitor</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Scheduled Date</TableHead>
                <TableHead>Completed Date</TableHead>
                <TableHead>Notes</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {displayedTests.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-4">
                    No tests found
                  </TableCell>
                </TableRow>
              ) : (
                displayedTests.map((test: VisitorTest) => {
                  const visitor = visitors.find(v => v.id === test.visitorId);
                  return (
                    <TableRow key={test.id}>
                      <TableCell className="font-medium">{test.testName}</TableCell>
                      <TableCell>
                        {visitor ? `${visitor.firstName} ${visitor.lastName}` : 'Unknown'}
                      </TableCell>
                      <TableCell>
                        <Badge variant={getStatusBadgeColor(test.status) as any}>
                          {getStatusLabel(test.status)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {test.scheduledDate ? formatUsDate(test.scheduledDate) : '-'}
                      </TableCell>
                      <TableCell>
                        {test.completedDate ? formatUsDate(test.completedDate) : '-'}
                      </TableCell>
                      <TableCell className="max-w-xs truncate">
                        {test.notes || '-'}
                      </TableCell>
                      <TableCell className="text-right space-x-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleOpenDialog(test)}
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        {!isClientRole && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(test.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-md max-h-screen overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingTest ? 'Edit Visitor Test' : 'Add New Visitor Test'}
            </DialogTitle>
            <DialogDescription>
              {editingTest
                ? 'Update test information'
                : 'Create a new visitor test record'}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-sm font-medium">Visitor *</label>
              <Select
                value={formData.visitorId.toString()}
                onValueChange={handleSelectVisitor}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {filteredVisitors.map((visitor: any) => (
                    <SelectItem key={visitor.id} value={visitor.id.toString()}>
                      {visitor.firstName} {visitor.lastName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium">Test Type *</label>
              <Select
                value={formData.testTypeId.toString()}
                onValueChange={handleSelectTest}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {tests.map((test: any) => (
                    <SelectItem key={test.id} value={test.id.toString()}>
                      {test.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium">Status *</label>
              <Select
                value={formData.status}
                onValueChange={(value) =>
                  setFormData({ ...formData, status: value })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="NOT_STARTED">Not Started</SelectItem>
                  <SelectItem value="WAITING_FOR_RESULT">Waiting For Result</SelectItem>
                  <SelectItem value="RESULT_UPLOADED">Result Uploaded</SelectItem>
                  <SelectItem value="READY_TO_REVIEW">Ready To Review</SelectItem>
                  <SelectItem value="CANCELED">Canceled</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium">Scheduled Date</label>
              <Input
                type="date"
                value={formData.scheduledDate || ''}
                onChange={(e) =>
                  setFormData({ ...formData, scheduledDate: e.target.value })
                }
              />
              <p className="text-xs text-slate-500 mt-1">
                Format: MM-DD-YYYY
                {formData.scheduledDate ? ` (Selected: ${formatUsDate(formData.scheduledDate)})` : ""}
              </p>
            </div>

            <div>
              <label className="text-sm font-medium">Completed Date</label>
              <Input
                type="date"
                value={formData.completedDate || ''}
                onChange={(e) =>
                  setFormData({ ...formData, completedDate: e.target.value })
                }
              />
              <p className="text-xs text-slate-500 mt-1">
                Format: MM-DD-YYYY
                {formData.completedDate ? ` (Selected: ${formatUsDate(formData.completedDate)})` : ""}
              </p>
            </div>

            <div>
              <label className="text-sm font-medium">Result Document URL</label>
              <Input
                value={formData.resultDocumentUrl || ''}
                onChange={(e) =>
                  setFormData({ ...formData, resultDocumentUrl: e.target.value })
                }
                placeholder="https://..."
              />
            </div>

            <div>
              <label className="text-sm font-medium">Notes</label>
              <textarea
                value={formData.notes || ''}
                onChange={(e) =>
                  setFormData({ ...formData, notes: e.target.value })
                }
                className="w-full p-2 border rounded-md text-sm"
                rows={3}
                placeholder="Add any notes about the test..."
              />
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={handleCloseDialog}
              >
                Cancel
              </Button>
              <Button type="submit">
                {editingTest ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VisitorTestsList;
